import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, Input, Output, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from "@angular/core";
import { AssignmentRecord } from "@playbook/data-models/records/assignment-record";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { LanguageSettings } from "@common/services/language-settings";
import { EnpsRecordControl } from "@common/controls/enps-record-control/enps-record-control";
import { DateTime } from "@common/data-models/complex-types/date-time";
import { Table } from "@common/data-models/complex-types/table";
import { FieldDef } from "@common/data-models/service/field-def";
import { FieldDefValidations } from "@common/enums/enums";
import { DateRange } from "@common/data-models/complex-types/date-range";
import { ObjectTypes } from "@common/enums/object-types";
import { PlaybookService } from "@playbook/services/playbook-service";
import { AppService } from "@suite/app.service";
import { MultiValue } from "../../data-models/complex-types/multi-value";
import { CdkConnectedOverlay } from "@angular/cdk/overlay";
import { NavigationDirection } from "../../enums/navigation-direction";
import { Subject, Subscription } from "rxjs";
import { MatAccordion } from "@angular/material/expansion";
import { StExpansionPanel } from "../../components/st-expansion-panel/st-expansion-panel.component";
import { VisualStateService } from "../../services/visual-state-service";
import { LayoutState } from '@suite/common/services/layout-state';


@Component({
    selector: "assignment-full-screen",
    templateUrl: "assignment-full-screen.html",
    styleUrls: ["assignment-full-screen.scss"],
    host: {
        class: "assignment-full-screen"
    },
    // changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class AssignmentFullScreenDialogComponent {
    @Output() public close: EventEmitter<void> = new EventEmitter<void>();
    @Output() public onAssignmentOpened: EventEmitter<number> = new EventEmitter()
    pstFieldNames = PSTFieldNames;
    @Input()
    fieldDefValidations = FieldDefValidations;
    isPanelOpen: boolean = false;
    assignmentEditable: boolean = true;
    indexInParent: number = 0;
    destroyed$ = new Subject<void>();
    @ViewChild('externalActions', { read: TemplateRef }) externalActions: TemplateRef<any>;
    @ViewChild(CdkConnectedOverlay) overlay: CdkConnectedOverlay;
    @ViewChild(MatAccordion) accordion: MatAccordion;
    NavigationDirection = NavigationDirection;
    recordChangedSubscription: Subscription = null;
    parentRecordChangedSubscription: Subscription = null;

    private _data: AssignmentRecord = null;
    public get data(): AssignmentRecord {
        return this._data;
    }
    public set data(v: AssignmentRecord) {
        this._data = v;
        if (v) {
            this.assignmentEditable = this._data?.getSecurityPrivsWrite(this.appService);
            if (this.data && this.data?.path && (this.data?.path?.isWasteBinPath || this.data?.parentRecord?.path?.isWasteBinPath)) {
                this.assignmentEditable = false;
            }
            if (this.recordChangedSubscription) this.recordChangedSubscription.unsubscribe();
            this.recordChangedSubscription = this._data?.changeSubject.subscribe(() => this.recordChanged());
            this.parentRecordChangedSubscription = this._data?.parentRecord.changeSubject.subscribe(() => this.parentRecordChanged());
        }
    }

    get publicationDateLabel(): string {
        return this.data?.fileDate?.toISODateTimeString();
    }

    get timeUpdatedLabel(): string {
        return `${this.data?.modTime?.moment?.fromNow()}`;
    }

    get attachmentsFiedGroupLabel() {
        const attachmentsFieldGroupLabel = 'PSTWebFieldGroup';
        return this.languageSettings.get(`${attachmentsFieldGroupLabel}_${this.languageSettings.PSTWebAttachmentsPane}`).includes(attachmentsFieldGroupLabel) ? this.languageSettings.PSTWebAttachmentsPane : this.languageSettings.get(`${attachmentsFieldGroupLabel}_${this.languageSettings.PSTWebAttachmentsPane}`)
    }

    get showAttachments(): boolean {
        return this.appService.attachmentsAllowed && !!this.data?.guid;
    }

    get groups(): Array<any> {
        return this.globalTablesService.getAllPropertyGroupsFor(this.getType());
    }

    get indexInParentToShow(): number {
        return this.indexInParent + 1;
    }

    get specialProps(): Array<FieldDef> {
        return this.globalTablesService.getSpecialPropertiesFor(this.getType()).filter((field) => {
            return field.ID !== PSTFieldNames.PSTExternalFiles && field.ID !== PSTFieldNames.Relationships;
        });
    }

    get enforceRequiredFieldsOnCreate(): boolean {
        return this.globalTablesService.EnforceRequiredFieldsOnCreate
    }

    get totalCount(): number {
        return this.data?.parentRecord?.children?.length ?? 0;
    }

    get collapseAllLabel(): string {
        return this.languageSettings?.PSTWebCollapseAllLabel;
    }

    get expandAllLabel(): string {
        return this.languageSettings?.PSTWebExpandAllLabel;
    }

    get isRTL() {
        return this.globalTablesService.getRtl();
    }
    get searchString() : string {
        return this.viewStateService.viewStateSearchQuery;
    }


    constructor(
        public changeDetectorRef: ChangeDetectorRef,
        public globalTablesService: GlobalTablesService,
        public languageSettings: LanguageSettings,
        public playbookService: PlaybookService,
        public visualStateService: VisualStateService,
        public viewStateService: LayoutState,
        public appService: AppService,
        private vref: ViewContainerRef
    ) {
    }

    async ngAfterViewInit() {
        this.vref.createEmbeddedView(this.externalActions);
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private getType(): string {
        let type: string = "Assignment";
        if (this.data.type == ObjectTypes.TOPIC) {
            type = "Topic";
        }
        if (this.data.type == ObjectTypes.ASSIGNMENT) {
            type = "Assignment";
        }
        if (this.data.type == ObjectTypes.DRAFT) {
            type = "Draft";
        }
        if (this.data.type == ObjectTypes.SCRIPT) {
            type = "Script";
        }
        if (this.data.type == ObjectTypes.WIRE) {
            type = "Wire";
        }
        return type;
    }


    updateAssignment(value: any, fieldId: string) {
        if (fieldId === PSTFieldNames.PSTAssignmentSlug) {
            this.data.assignmentSlug = value.trim();
        }
        if (fieldId == PSTFieldNames.PSTTopicEventStartDate) {
            const castedVal = value as DateTime;
            if (castedVal?.get() > this.data.getAs<DateTime>(PSTFieldNames.PSTTopicEventEndDate)?.get()) {
                EnpsRecordControl.saveChanges(new DateTime(castedVal?.get()), PSTFieldNames.PSTTopicEventEndDate, this.data);
            }
        }
        EnpsRecordControl.saveChanges(value, fieldId, this.data);
    }

    public isGroupSingleValue(property: string): boolean {
        return this.globalTablesService.isGroupSingleValue && (property == PSTFieldNames.PSTAssignmentPublishingDesk || property == PSTFieldNames.PSTTopicDepartment);
    }

    public getDateTimeValue(id: string): DateTime {
        return this.data.getAs<DateTime>(id, new DateTime());
    }

    public getDateRangeValue(id: string): DateRange {
        return this.data.getAs<DateRange>(id, new DateRange());
    }

    public getMinDate(id: string): DateTime {
        if(id == PSTFieldNames.PSTTopicEventEndDate) {
            return this.data.getAs<DateTime>(PSTFieldNames.PSTTopicEventStartDate);
        }
    }

    public getList(group: string): Array<FieldDef> {
        return this.globalTablesService.getDynamicPropertiesFor(this.getType(), group).filter(p => p.ID !== PSTFieldNames.PSTTeamLocation);
    }

    public getStaffIdValue(id: string): MultiValue<string> {
        return this.data.getAs<MultiValue<string>>(id, new MultiValue<string>());
    }

    public getCheckListValue(id: string): MultiValue<string> {
        return this.data.getAs<MultiValue<string>>(id, new MultiValue<string>());
    }

    public getFieldGroupsLabel(group: any) {
        const fieldGroupLabel = 'PSTWebFieldGroup';
        return this.languageSettings.get(`${fieldGroupLabel}_${group.name == '' ? 'Properties' : group.name}`).includes(fieldGroupLabel) ? (group.name == '' ? 'Properties' : group.name) : this.languageSettings.get(`${fieldGroupLabel}_${group.name == '' ? 'Properties' : group.name}`);
    }

    public getValue(id: string, validation: string): any {

        if (!this.data.get(id)) {
            if ((validation.toUpperCase() == FieldDefValidations.SPECIAL) || (validation.toUpperCase() == FieldDefValidations.COST)) {
                this.data.set(id, new Table());
            }
        }
        const value = this.data.getAs<Table>(id);
        value.colMetadata = this.globalTablesService.getListColumnsForSpecialField(id);
        value.addEmptyRowIfNeeded();
        return value;
    }

    public onMouseOut(event: MouseEvent) {
        if (this.visualStateService.isTouchDevice) return;
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-external-actions-overlay')) {
            this.isPanelOpen = false;
        }
    }

    externalActionsMouseEnter(event: MouseEvent) {
        this.isPanelOpen = true;
    }

    panelAttached() {
        const overlayElement = this.overlay?.overlayRef?.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener("mouseleave", (e) => {
                this.isPanelOpen = false;
            });
        }
    }

    public navigate(event, direction: NavigationDirection) {
        switch (direction) {
            case NavigationDirection.FIRST:
                this.data = this.data.parentRecord.children[0] as AssignmentRecord;
                this.indexInParent = 0;
                break;
            case NavigationDirection.PREVIOUS:
                {
                    const prevRec = this.data.parentRecord.children[this.indexInParentToShow - 2] as AssignmentRecord;
                    this.indexInParent = this.indexInParentToShow - 2;
                    if (prevRec) {
                        this.data = prevRec;
                    } else {
                        this.data = this.data.parentRecord.children[this.totalCount - 1] as AssignmentRecord;
                        this.indexInParent = this.totalCount - 1;
                    }
                }
                break;
            case NavigationDirection.NEXT:
                {
                    const nextRec = this.data.parentRecord.children[this.indexInParentToShow] as AssignmentRecord;
                    this.indexInParent = this.indexInParentToShow;
                    if (nextRec) {
                        this.data = nextRec;
                    } else {
                        this.data = this.data.parentRecord.children[0] as AssignmentRecord;
                        this.indexInParent = 0;
                    }
                }
                break;
            case NavigationDirection.LAST:
                this.data = this.data.parentRecord.children[this.totalCount - 1] as AssignmentRecord;
                this.indexInParent = this.totalCount - 1;
                break;
        }
        this.onAssignmentOpened.emit(this.indexInParent);
    }

    @HostListener('document:keyup', ['$event'])
    public navigateForward(event) {

        if (event &&
            event.target &&
            event.target.tagName &&
            !(
                event.target.tagName.toLowerCase() == 'input' ||
                event.target.tagName.toLowerCase() == 'textarea'
            )) {
            if (event.code === 'Period') {
                this.navigate(event, NavigationDirection.NEXT)
            } else if (event.code === 'Comma') {
                this.navigate(event, NavigationDirection.PREVIOUS)
            }
        }
    }

    expandCollapseAll(paneContainer: StExpansionPanel) {
        if (paneContainer.expanded) {
            this.accordion.closeAll();
        } else {
            this.accordion.openAll();
        }
    }

    panelStateChanged($event: boolean, location: string, title: string) {
        this.visualStateService.saveExpanderSettings(location, title, $event);
    }

    recordChanged() {
        this.assignmentEditable = this.data?.getSecurityPrivsWrite(this.appService);
        if (this.data?.path?.isWasteBinPath || this.data?.parentRecord?.path?.isWasteBinPath) {
            this.assignmentEditable = false;
        }
    }

    public parentRecordChanged() {
        if (this.data) {
            if (this.data?.parentRecord?.path?.isWasteBinPath) {
                this.assignmentEditable = false;
            }
        }
    }
}
